/**
 * The shape `map_view.py` writes and the geometry the Tree renderer draws it in. Kept free of JSX so
 * the layout is tested on numbers: which row a leaf lands on, where a branch ends, what a hover says.
 */

export type NodeState = "locked" | "open" | "done";

/** What `concept_state.py` worked out for a concept, off every project's records. */
export type ConceptState = "known" | "weak" | "untested";

export interface TreeConcept {
	slug: string;
	name: string;
	state: ConceptState;
}

export interface TreeSection {
	id: string;
	name: string;
	state: NodeState;
	weak: boolean;
	status: string;
	learned: string;
	/** A section inserted to teach a prerequisite the stage's opening found missing. */
	detour: boolean;
	/** The concepts it teaches, present on the active stage's sections only; a twig each. */
	concepts: TreeConcept[];
}

export interface TreeStage {
	id: string;
	name: string;
	state: NodeState;
	status: string;
	task: string;
	sections: TreeSection[];
}

export interface TreeView {
	topic: string;
	stages: TreeStage[];
}

/**
 * One section, drawn as a pill hanging off its stage's branch, or one of its concepts, a smaller pill
 * on the row under it. `first` opens a row.
 */
export interface Leaf {
	id: string;
	kind: "section" | "concept";
	x: number;
	y: number;
	w: number;
	h: number;
	label: string;
	state: NodeState;
	weak: boolean;
	title: string;
	first: boolean;
}

export interface StageBox {
	id: string;
	x: number;
	y: number;
	label: string;
	state: NodeState;
	title: string;
	/** The line its leaves hang off: from the stage node down to the last row. */
	branch: { x: number; y1: number; y2: number };
	leaves: Leaf[];
}

export interface TreeLayout {
	width: number;
	height: number;
	/** `title` carries the whole topic when the label had to be cut. */
	root: { x: number; y: number; label: string; title: string };
	/** The line the stage nodes sit on, from the root to the last stage. */
	trunk: { x: number; y1: number; y2: number };
	stages: StageBox[];
}

const STATES: readonly NodeState[] = ["locked", "open", "done"];
const CONCEPT_STATES: readonly ConceptState[] = ["known", "weak", "untested"];

/** How a concept is lit, and what its hover says. */
const CONCEPT_LOOK: Record<ConceptState, { state: NodeState; weak: boolean; title: string }> = {
	known: { state: "done", weak: false, title: "已会" },
	weak: { state: "open", weak: true, title: "薄弱" },
	untested: { state: "locked", weak: false, title: "未测试" },
};
const DETOUR = "补课";

const TRUNK_X = 10;
const BRANCH_X = 26;
const LEAF_X = 40;
const ROOT_Y = 14;
const STAGE_GAP = 34;
const LEAF_H = 22;
const LEAF_ROW = 30;
const LEAF_GAP = 8;
const LEAF_PAD = 24;
const MIN_LEAF_W = 40;
const TWIG_X = LEAF_X + 16;
const TWIG_H = 18;
const TWIG_ROW = 24;
const TWIG_PAD = 20;
const LABEL_X = 14;
const LOCK_W = 20;
const ELLIPSIS = "…";

/** Roughly how wide the label draws at 13px: a CJK character is square, the rest about half. */
export function measureText(text: string): number {
	let w = 0;
	for (const ch of text) w += ch.charCodeAt(0) > 0x2e7f ? 13 : 7;
	return w;
}

function str(v: unknown): string {
	return typeof v === "string" ? v : "";
}

function isState(v: unknown): v is NodeState {
	return typeof v === "string" && (STATES as readonly string[]).includes(v);
}

function parseConcept(raw: unknown): TreeConcept | null {
	const c = raw as Partial<TreeConcept> | null;
	if (!c || typeof c !== "object" || typeof c.slug !== "string" || typeof c.name !== "string") return null;
	if (typeof c.state !== "string" || !(CONCEPT_STATES as readonly string[]).includes(c.state)) return null;
	return { slug: c.slug, name: c.name, state: c.state };
}

/** A view written before concept leaves has no `concepts` and no `detour`; both read as none. */
function parseSection(raw: unknown): TreeSection | null {
	const s = raw as Partial<TreeSection> | null;
	if (!s || typeof s !== "object" || typeof s.id !== "string" || typeof s.name !== "string" || !isState(s.state)) return null;
	const concepts: TreeConcept[] = [];
	for (const entry of Array.isArray(s.concepts) ? s.concepts : []) {
		const concept = parseConcept(entry);
		if (!concept) return null;
		concepts.push(concept);
	}
	return { id: s.id, name: s.name, state: s.state, weak: s.weak === true, status: str(s.status), learned: str(s.learned), detour: s.detour === true, concepts };
}

function parseStage(raw: unknown): TreeStage | null {
	const s = raw as Partial<TreeStage> | null;
	if (!s || typeof s !== "object" || typeof s.id !== "string" || typeof s.name !== "string" || !isState(s.state)) return null;
	if (!Array.isArray(s.sections)) return null;
	const sections: TreeSection[] = [];
	for (const entry of s.sections) {
		const section = parseSection(entry);
		if (!section) return null;
		sections.push(section);
	}
	return { id: s.id, name: s.name, state: s.state, status: str(s.status), task: str(s.task), sections };
}

export function parseTree(raw: unknown): { ok: true; tree: TreeView } | { ok: false; error: string } {
	const v = raw as Partial<TreeView> | null;
	if (!v || typeof v !== "object" || !Array.isArray(v.stages)) return { ok: false, error: "不是地图树：缺少 stages" };
	const stages: TreeStage[] = [];
	for (const entry of v.stages) {
		const stage = parseStage(entry);
		if (!stage) return { ok: false, error: "地图中有里程碑或小节无法读取" };
		stages.push(stage);
	}
	return { ok: true, tree: { topic: str(v.topic), stages } };
}

/** The label, cut with an ellipsis when it would not fit in `max`. */
function fit(text: string, max: number, measure: (t: string) => number): string {
	if (measure(text) <= max) return text;
	const chars = [...text];
	while (chars.length > 1 && measure(chars.join("") + ELLIPSIS) > max) chars.pop();
	return chars.join("") + ELLIPSIS;
}

function join(...parts: string[]): string {
	return parts.filter((p) => p !== "").join(" · ");
}

/** Concept twigs on the rows under their section, indented; returns the y of the last twig row. */
function layoutTwigs(section: TreeSection, top: number, width: number, measure: (t: string) => number, leaves: Leaf[]): number {
	const maxW = Math.max(MIN_LEAF_W, width - TWIG_X);
	let x = TWIG_X;
	let y = top;
	for (const c of section.concepts) {
		const w = Math.min(maxW, measure(c.name) + TWIG_PAD);
		if (x > TWIG_X && x + w > width) {
			x = TWIG_X;
			y += TWIG_ROW;
		}
		const label = fit(c.name, w - TWIG_PAD, measure);
		const look = CONCEPT_LOOK[c.state];
		leaves.push({
			id: `${section.id}/${c.slug}`,
			kind: "concept",
			x,
			y,
			w,
			h: TWIG_H,
			label,
			state: look.state,
			weak: look.weak,
			title: label === c.name ? look.title : `${c.name}\n${look.title}`,
			first: x === TWIG_X,
		});
		x += w + LEAF_GAP;
	}
	return y;
}

function layoutLeaves(stage: TreeStage, top: number, width: number, measure: (t: string) => number): Leaf[] {
	const maxW = Math.max(MIN_LEAF_W, width - LEAF_X);
	const leaves: Leaf[] = [];
	let x = LEAF_X;
	let y = top;
	for (const s of stage.sections) {
		const w = Math.min(maxW, measure(s.name) + LEAF_PAD);
		if (x > LEAF_X && x + w > width) {
			x = LEAF_X;
			y += LEAF_ROW;
		}
		const label = fit(s.name, w - LEAF_PAD, measure);
		const title = join(s.detour ? DETOUR : "", s.status, s.learned);
		leaves.push({
			id: s.id,
			kind: "section",
			x,
			y,
			w,
			h: LEAF_H,
			label,
			state: s.state,
			weak: s.weak,
			title: label === s.name ? title : `${s.name}\n${title}`,
			first: x === LEAF_X,
		});
		x += w + LEAF_GAP;
		// Its concepts take the rows under it, and the next section opens a row of its own.
		if (s.concepts.length) {
			y = layoutTwigs(s, y + TWIG_ROW, width, measure, leaves) + LEAF_ROW;
			x = LEAF_X;
		}
	}
	return leaves;
}

export function layoutTree(tree: TreeView, width: number, measure: (t: string) => number = measureText): TreeLayout {
	const stages: StageBox[] = [];
	const labelMax = width - TRUNK_X - LABEL_X;
	let y = ROOT_Y + STAGE_GAP;
	for (const stage of tree.stages) {
		const leaves = layoutLeaves(stage, y + LEAF_ROW, width, measure);
		const last = leaves.length ? leaves[leaves.length - 1]!.y : y;
		const label = fit(stage.name, labelMax - (stage.state === "locked" ? LOCK_W : 0), measure);
		const title = join(stage.status, stage.task);
		stages.push({
			id: stage.id,
			x: TRUNK_X,
			y,
			label,
			state: stage.state,
			title: label === stage.name ? title : `${stage.name}
${title}`,
			branch: { x: BRANCH_X, y1: y, y2: last },
			leaves,
		});
		y = last + STAGE_GAP;
	}
	const lastStageY = stages.length ? stages[stages.length - 1]!.y : ROOT_Y;
	const rootLabel = fit(tree.topic, labelMax, measure);
	return {
		width,
		height: y - STAGE_GAP + LEAF_H,
		root: { x: TRUNK_X, y: ROOT_Y, label: rootLabel, title: rootLabel === tree.topic ? "" : tree.topic },
		trunk: { x: TRUNK_X, y1: ROOT_Y, y2: lastStageY },
		stages,
	};
}
